<template>
  <div class="ep-page">

    <!-- ── Filters ────────────────────────────────────────── -->
    <div class="ep-card p-4 sm:p-5">
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 items-end">
        <div>
          <label class="ep-label">{{ t('empPayslips.year') }}</label>
          <select v-model="filters.year" @change="fetchPayslips" class="ep-select">
            <option value="">{{ t('empPayslips.allYears') }}</option>
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
        <div>
          <label class="ep-label">{{ t('empPayslips.month') }}</label>
          <select v-model="filters.month" @change="fetchPayslips" class="ep-select">
            <option value="">{{ t('empPayslips.allMonths') }}</option>
            <option v-for="(name, idx) in monthNames" :key="idx + 1" :value="idx + 1">{{ name }}</option>
          </select>
        </div>
        <div>
          <label class="ep-label">{{ t('common.status') }}</label>
          <select v-model="filters.status" @change="fetchPayslips" class="ep-select">
            <option value="">{{ t('empPayslips.allStatuses') }}</option>
            <option value="finalized">{{ t('empPayslips.finalized') }}</option>
            <option value="paid">{{ t('empPayslips.paid') }}</option>
          </select>
        </div>
        <div>
          <button v-if="hasActiveFilters" @click="clearFilters" class="ep-btn ep-btn-secondary w-full sm:w-auto">
            {{ t('empPayslips.clear') }}
          </button>
        </div>
      </div>

      <p v-if="hasActiveFilters" class="mt-3 text-xs text-indigo-400">
        {{ t('empPayslips.filtered') }} · {{ payslips.length }} {{ t('empPayslips.payslipsFound') }}
      </p>
    </div>

    <!-- ── Loading ────────────────────────────────────────── -->
    <div v-if="loading" class="py-16"><div class="ep-spinner"></div></div>

    <!-- ── Error ──────────────────────────────────────────── -->
    <div v-else-if="error"
         class="rounded-2xl px-5 py-4 text-sm text-red-300"
         style="background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.25)">
      {{ error }}
    </div>

    <!-- ── Payslip list ───────────────────────────────────── -->
    <div v-else-if="payslips.length > 0" class="space-y-3">
      <button
        v-for="payslip in payslips"
        :key="payslip.id"
        @click="viewPayslip(payslip.id)"
        class="ep-card ep-card-hover w-full text-left p-5 group"
      >
        <div class="flex items-center gap-4">
          <div class="ep-stat-icon ep-stat-indigo hidden sm:flex">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2.5 flex-wrap">
              <h3 class="text-base font-semibold text-slate-100 group-hover:text-white transition-colors">
                {{ getMonthName(payslip.month) }} {{ payslip.year }}
              </h3>
              <span :class="getStatusClass(payslip.status)">
                <span class="ep-dot"></span>{{ getStatusText(payslip.status) }}
              </span>
            </div>
            <p class="mt-1 text-xs text-slate-500">
              {{ formatDate(payslip.period_start_date) }} – {{ formatDate(payslip.period_end_date) }}
            </p>
            <p v-if="payslip.status === 'paid'" class="mt-1 text-xs text-emerald-400">
              ✓ {{ t('empPayslips.paidOn') }} {{ formatDate(payslip.paid_at) }}
            </p>
          </div>

          <div class="text-right shrink-0">
            <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">{{ t('empPayslips.netPay') }}</p>
            <p class="text-xl sm:text-2xl font-bold text-slate-100 tabular-nums">
              {{ formatCurrency(payslip.final_salary) }}
            </p>
          </div>
        </div>
      </button>
    </div>

    <!-- ── Empty ──────────────────────────────────────────── -->
    <div v-else class="ep-card">
      <div class="ep-empty">
        <svg class="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p class="text-sm font-medium text-slate-400">
          {{ hasActiveFilters ? t('empPayslips.noResults') : t('empPayslips.noPayslips') }}
        </p>
        <p class="text-xs text-slate-600 mt-1">
          {{ hasActiveFilters ? t('empPayslips.adjustFilters') : t('empPayslips.noRecordsYet') }}
        </p>
        <button v-if="hasActiveFilters" @click="clearFilters"
                class="mt-3 text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors">
          {{ t('empPayslips.clearFilters') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { api } from '@/services/api';
import { useCurrency } from '@/composables/useCurrency';
const { t } = useI18n();

const router = useRouter();
const { formatCurrency, loadCurrency } = useCurrency();

const payslips = ref([]);
const loading = ref(true);
const error = ref(null);

const filters = ref({ year: '', month: '', status: '' });

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

const hasActiveFilters = computed(() =>
  filters.value.year !== '' || filters.value.month !== '' || filters.value.status !== ''
);

onMounted(async () => {
  loadCurrency();
  await fetchPayslips();
});

async function fetchPayslips() {
  try {
    loading.value = true;
    error.value = null;

    const params = {};
    if (filters.value.year) params.year = filters.value.year;
    if (filters.value.month) params.month = filters.value.month;
    if (filters.value.status) params.status = filters.value.status;

    const response = await api.get('/employee/payslips', { params });

    if (response.data?.success) {
      payslips.value = response.data.data?.payslips || [];
    } else {
      throw new Error(response.data?.error || 'Failed to fetch payslips');
    }
  } catch (err) {
    console.error('Error fetching payslips:', err);
    error.value = err.response?.data?.error || err.message || 'Failed to load payslips';
  } finally {
    loading.value = false;
  }
}

function clearFilters() {
  filters.value = { year: '', month: '', status: '' };
  fetchPayslips();
}

function viewPayslip(id) {
  router.push({ name: 'EmployeePayslipDetail', params: { id } });
}

function getMonthName(month) {
  return monthNames[month - 1] || 'Unknown';
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function getStatusText(status) {
  const statusMap = {
    draft: 'Draft',
    finalized: 'Finalized',
    paid: 'Paid'
  };
  return statusMap[status] || status;
}

function getStatusClass(status) {
  const classMap = {
    draft: 'ep-badge ep-badge-neutral',
    finalized: 'ep-badge ep-badge-info',
    paid: 'ep-badge ep-badge-success'
  };
  return classMap[status] || 'ep-badge ep-badge-neutral';
}
</script>
